import React from 'react';
import { useRecordContext } from 'react-admin';
import { Box, Typography, Chip, Divider, Paper } from '@mui/material';
import { CopyIcon, ImageWithZoom } from '@troverecommerce/saas-components-ts';
import { DashboardCurrency } from '@src/utils/valueFormatters';
import type { RepairDetail, RepairItemType, RepairItemIssueType } from '@src/types';

const ShowItemsSection = () => {
  const record = useRecordContext<RepairDetail>();

  if (!record || !record.repairItems) return null;

  return (
    <Paper>
      <Box sx={{ padding: 2 }}>
        {record.repairItems.map((item: RepairItemType, index: number, arr: RepairItemType[]) => (
          <Box key={item.uuid || index} sx={{ marginBottom: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: 1 }}>
              <Typography variant="h6">
                Item:
                {' '}
                {item.uuidPrefix}
              </Typography>
              <CopyIcon name="Repair ID" value={item.uuidPrefix} />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 1, marginTop: 1 }}>
              <Typography variant="body2" fontWeight="bold">Item:</Typography>
              <Typography variant="body2">{item.title || 'N/A'}</Typography>

              {item.problemTypes && item.problemTypes.length > 0 && (
                <>
                  <Typography variant="body2" fontWeight="bold">Repairs Requested:</Typography>
                  <Box>
                    {item.problemTypes.map((type, idx) => (
                      <Chip key={idx} label={type} size="small" sx={{ marginRight: 1, marginBottom: 0.5 }} />
                    ))}
                  </Box>
                </>
              )}

              {item.notes && (
                <>
                  <Typography variant="body2" fontWeight="bold">Customer Notes:</Typography>
                  <Typography variant="body2">{item.notes}</Typography>
                </>
              )}
            </Box>

            {item.imageUrls && (
              <Box sx={{ marginTop: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Customer Photos:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {Object.values(item.imageUrls as Record<string, string>).map(url => (
                    <Paper key={url}>
                      <ImageWithZoom
                        imageUrl={url}
                        size={{ image: '150px' }}
                        maxZoomWidth="lg"
                      />
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}

            {item.repairItemIssues && item.repairItemIssues.length > 0 && (
              <Box>
                <Divider sx={{ marginY: 2 }} />
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Issues:
                </Typography>
                {item.repairItemIssues.map((issue: RepairItemIssueType, issueIdx: number) => (
                  <Paper key={issue.uuid || issueIdx} variant="outlined" sx={{ marginBottom: 2, padding: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 1 }}>
                      <Typography variant="body2" fontWeight="bold">Repair Requested:</Typography>
                      <Typography variant="body2">{issue.problem || 'N/A'}</Typography>

                      <Typography variant="body2" fontWeight="bold">Current Status:</Typography>
                      <Typography variant="body2">{issue.status || 'N/A'}</Typography>

                      <Typography variant="body2" fontWeight="bold">Repair Fee:</Typography>
                      <Typography variant="body2">
                        {DashboardCurrency.displayValue(issue.price, record.currencyCode)}
                      </Typography>

                      {issue.notes && (
                        <>
                          <Typography variant="body2" fontWeight="bold">Customer Note:</Typography>
                          <Typography variant="body2">{issue.notes}</Typography>
                        </>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default ShowItemsSection;
